import {
  type AuditLogActions,
  type AuditLogList,
  auditLogActionsSchema,
  auditLogListSchema,
} from '@xphoto/shared';
import { apiFetch } from '../lib/http';
import { AdminApiError } from './admin-api';

/**
 * Client typé du JOURNAL D'AUDIT (audit B1) — superadmin. Comme les autres clients du
 * front, chaque réponse est RE-PARSÉE par son schéma partagé (« never trust the
 * client », y compris ses propres réponses).
 *
 * Pas de fonction de purge, volontairement : l'API n'en expose pas (un journal d'audit
 * effaçable par la personne qu'il trace ne prouve rien — cf. `routes/audit-log.ts`).
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : code générique.
    }
    throw new AdminApiError(res.status, code);
  }
  return res.json();
}

/** Filtres composables du journal (tous optionnels, combinés en ET côté serveur). */
export interface AuditLogFilters {
  action?: string;
  userId?: string;
  /** Borne basse INCLUSIVE (ISO 8601 avec offset). */
  from?: string;
  /** Borne haute EXCLUSIVE (ISO 8601 avec offset). */
  to?: string;
}

/** `GET /api/admin/audit-log` — page `{ entries, total }` du journal d'audit. */
export async function fetchAuditLog(
  limit: number,
  offset: number,
  filters: AuditLogFilters = {},
  signal?: AbortSignal,
): Promise<AuditLogList> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (filters.action !== undefined) params.set('action', filters.action);
  if (filters.userId !== undefined) params.set('userId', filters.userId);
  if (filters.from !== undefined) params.set('from', filters.from);
  if (filters.to !== undefined) params.set('to', filters.to);
  const res = await apiFetch(`/api/admin/audit-log?${params.toString()}`, {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return auditLogListSchema.parse(await readJson(res));
}

/** `GET /api/admin/audit-log/actions` — verbes présents, pour la liste déroulante. */
export async function fetchAuditActions(signal?: AbortSignal): Promise<AuditLogActions> {
  const res = await apiFetch('/api/admin/audit-log/actions', {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return auditLogActionsSchema.parse(await readJson(res));
}
